import { HttpClient, HttpContext, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { IS_CALENSO_AUTH_REQUIRED } from '../constants/http-context';
import { WIDGET_CONSTANTS } from '../constants/widget-constants';
import { PartnerSettingDBModel, PayPalSettingsDbModel, VerifyPaymentStatusModel } from '../db-models/partner-setting.model';
import { UserDao } from '../db-models/user-data-dao';
import { CustomBookingMessageTemplate, FeaturesDao, PartnerDao } from '../db-models/widget-conf-dao';
import { AdyenBookingContext } from '../models/adyen.model';
import { IPartnerInformationBody } from '../models/partners';
import { CssOverwrites, CUSTOM_CSS_WIDGET_TYPE } from '../models/widget-conf';
import { AuthTokenService } from './auth-token.service';
import { CryptoService } from './crypto.service';
import { LocalStorageService } from './local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class PartnerService {

  private http = inject(HttpClient);
  private localStorageService = inject(LocalStorageService);
  private cryptoService = inject(CryptoService);
  private authTokenService = inject(AuthTokenService);

  protected _partnerVerificationCode = undefined;
  protected featuresList: { [key: string]: boolean } = {};

  set partnerVerificationCode(token: string) {
    this._partnerVerificationCode = token;
  }
  get partnerVerificationCode(): string {
    return this._partnerVerificationCode;
  }

  partnerUUID: string;

  // True when the widget is hosted by the booking-page app — set from the widget's
  // `channel` attribute via setBookingChannel(); drives the RR-v2 booking_link channel.
  isFromBookingPage = false;

  /** Booking-page embeds the widget with channel="booking_page"; standalone embeds set channel="booking_widget". */
  setBookingChannel(channel: string | undefined): void {
    this.isFromBookingPage = channel === 'booking_page';
  }

  protected getPartnerInfoFromLS(bookingName: string): PartnerDao | null {
    try {
      // get booking_page_partner_object setting information from the booking page
      const encryptedPartnerObj = this.localStorageService.getItemLocally(WIDGET_CONSTANTS.BOOKINGPAGE_PARTNER_OBJECT + bookingName);
      const partnerObj = JSON.parse(this.cryptoService.decryptValue(encryptedPartnerObj)) as PartnerDao;
      return partnerObj?.uuid ? partnerObj : null;
    } catch (e) {
      return null;
    }
  }

  getWidgetConf(body: IPartnerInformationBody): Observable<PartnerDao> {
    const partnerObj = this.getPartnerInfoFromLS(body.booking_name);

    if (partnerObj?.token) {
      this.authTokenService.setAuthToken(partnerObj.token);
      this.loadSubscriptionFeatures(partnerObj.features);
      return of(partnerObj);
    }

    return this.http.post<PartnerDao>(environment.apiUrl + 'partners/information', body, {
      context: new HttpContext().set(IS_CALENSO_AUTH_REQUIRED, true)
    }).pipe(
      switchMap(partner => {
        this.loadSubscriptionFeatures(partner.features);
        return of(partner);
      })
    );
  }

  protected loadSubscriptionFeatures(features: FeaturesDao[]): void {
    if (features?.length > 0) {
      features.forEach(feature => {
        this.featuresList[feature.feature.name] = true;
      });
    }
  }

  hasFeature(feature: string): boolean {
    return this.featuresList[feature] ? this.featuresList[feature] : false;
  }

  getWidgetTemplates(): Observable<CustomBookingMessageTemplate[]> {
    return this.http.get<CustomBookingMessageTemplate[]>(environment.apiUrl + 'templates/widget');
  }



  getCssOverwrites(): Observable<CssOverwrites> {
    return this.http.get<CssOverwrites>(`${environment.apiUrl}css_overwrites/active?type=${CUSTOM_CSS_WIDGET_TYPE.widget}`, {}).pipe(
      catchError(() => of(null))
    );
  }

  getCssOverwriteUsingUuid(uuid: string, isPreview = false): Observable<CssOverwrites> {
    let params = new HttpParams();
    if (isPreview) { params = params.append('preview', true); }

    return this.http.get<CssOverwrites>(environment.apiUrl + 'css_overwrites/' + uuid, { params }).pipe(
      catchError(() => of(null)),
      map(result => result?.errors !== undefined ? null : result)
    );
  }

  getWorker(uuid: string): Observable<UserDao> {
    return this.http.get(environment.apiUrl + 'workers/' + uuid).pipe(
      map(resp => {
        if (resp?.[0]) {
          return resp[0];
        }
      }));
  }

  getPartnerSettings(settingId: number): Observable<PartnerSettingDBModel[]> {
    const body = { setting_id: { value: settingId, operator: '=' } };
    return this.http.post<PartnerSettingDBModel[]>(environment.apiUrl + 'partners_settings/filter', body);
  }


  /**
   * Without a context: which providers the partner can use at all. With one: whether they can take
   * the money for THAT booking - e.g. whether its store has an Adyen account.
   */
  verifyOnlinePaymentStatus(context?: AdyenBookingContext): Observable<VerifyPaymentStatusModel> {
    return this.http.get<VerifyPaymentStatusModel>(
      environment.apiUrl + 'partners/verify_online_payment_status',
      context ? { params: { ...context } } : {},
    );
  }

  getPaypalSettings(): Observable<PayPalSettingsDbModel> {
    return this.http.get<PayPalSettingsDbModel>(environment.apiUrl + 'paypal/access_token');
  }

  verifyInternalToken(token: string): Observable<{ success: boolean }> {
    return this.http.post<{ success: boolean }>(environment.apiUrl + 'partners/verify_internal_token', { token }, {}).pipe(
      catchError(error => of(null))
    );
  }

  verifyWidgetPassword(param: { password: string }): Observable<{ success: boolean, code: number, message: string }> {
    return this.http.post<{ success: boolean, code: number, message: string }>(environment.apiUrl + 'partners_settings/verify_widget', param, {})
  }
}
